import axios from "axios";
import { REACT_APP_POLICY_API } from "../constants";
import { checkAPISuccess, manageServiceError } from "../utils/helpers";
import { requestsFor } from "./headerService";

const instance = axios.create({ baseURL: REACT_APP_POLICY_API });
const requests = requestsFor(instance);

// SSH policy is wired end to end:
//   proxy → authn-service → authnull-service /policy/json/searchPolicy
//                            └─► internal/sshpolicy.GetEffectivePolicy
// A policy matches on email, targetHost, targetAccount and jumpServerId
// (dto.SSHAuth). Only Approved policies are matched; create and update both
// store Approved.
const path = {
  createPolicy: "/api/v1/policy/json/createPolicy",
  listPolicy: "/api/v1/policy/json/listPolicy",
  updatePolicy: "/api/v1/policy/json/updatePolicy",
  deletePolicy: "/api/v1/policy/json/deletePolicy",
  revokePolicy: "/api/v1/policy/json/revokePolicy",
  policyAction: "/api/v1/policy/json/policyAction",
  searchPolicy: "/api/v1/policy/json/searchPolicy",
};

// Delete and suspend end the policy's SSH grants (revoke their keys) first. If
// that fails the request fails with this message and the policy is unchanged,
// so the UI shows it and offers a retry instead of a generic error.
export const GRANTS_END_FAILED = "failed to end the policy's ssh grants";
export const errorText = (err) => err?.data?.error || err?.data?.message || err?.message || "";

export const SSH_CREDENTIAL_TYPE = "SSH";

// SSH is deny-by-default (#338): a session no policy covers is refused, and
// when several match the most specific wins. Any screen listing SSH policies
// has to say so, because an empty list means nobody can connect.
export const NO_POLICY_MEANS_ALLOW = false;

// policyFlow values the policy engine understands. Always send one: create
// fills in mfa_required when it is blank, but update stores policyJson as
// given, so an edit without policyFlow would quietly become allow.
export const SSH_POLICY_FLOWS = [
  { value: "mfa_required", label: "Require approval" },
  { value: "block", label: "Deny" },
  // No second factor at all — offered to administrators only.
  { value: "allow", label: "Allow without approval", adminOnly: true },
];
// Short form for list badges.
export const SSH_POLICY_FLOW_BADGE = { mfa_required: "Require approval", block: "Deny", allow: "Allow" };
export const DEFAULT_SSH_POLICY_FLOW = "mfa_required";

// update and delete answer 200 without a `code` field ({ updatedPolicyID }),
// so success is the HTTP status unless the body reports a failure code.
const bodyOk = (body) => !body || body.code === undefined || (body.code >= 200 && body.code < 300);

// Group-scoped policies do not fire yet: searchSSHPolicy passes nil for groups
// on purpose, so an unresolved group is a missing feature rather than a wrong
// answer. A policy written against a group will silently never match.
export const GROUP_SCOPE_SUPPORTED = false;

export async function createSSHPolicy({ payload = {}, onSuccess, onFailure, onConflict }) {
  try {
    const { data: response } = await requests.post(path.createPolicy, {
      // Required (binding:"required"), and exactly "SSH": the list filter is
      // case-sensitive.
      policyType: SSH_CREDENTIAL_TYPE,
      ...payload,
    });
    if (checkAPISuccess(response)) onSuccess && onSuccess(response);
    else onFailure && onFailure(response);
  } catch (error) {
    const status = error?.response?.status;
    const body = error?.response?.data;
    if (status === 409 && onConflict) {
      onConflict({ conflicts: body?.conflicts || [], warning: body?.warning || body?.message || "", raw: body });
      return;
    }
    manageServiceError(error, onFailure, "Create Endpoint Policy Failed");
  }
}

// Lists SSH policies, optionally only those scoped to one gateway. The server
// filters on policyType (the only SSH-relevant filter it reads); the gateway
// is matched client-side on policyJson.ssh.jumpServers. Resolves with the rows.
export async function listSSHPolicy({ orgId, tenantId, jumpServerId, onSuccess, onFailure }) {
  try {
    const { data: response } = await requests.post(path.listPolicy, {
      orgId,
      tenantId,
      page: 1,
      limit: 100,
      filters: [{ filterParameter: "policyType", filterValue: SSH_CREDENTIAL_TYPE }],
    });
    if (!checkAPISuccess(response)) return onFailure && onFailure(response);
    // The server already filters on policyType; checked again here so a
    // non-SSH row can never render as an SSH policy.
    const rows = (Array.isArray(response?.data) ? response.data : []).filter(
      (r) => r?.policyType === SSH_CREDENTIAL_TYPE,
    );
    const scoped = jumpServerId
      ? rows.filter((r) =>
          (r?.policyJson?.ssh?.jumpServers || []).map(Number).includes(Number(jumpServerId)),
        )
      : rows;
    onSuccess && onSuccess(scoped, response);
  } catch (error) {
    manageServiceError(error, onFailure, "List Endpoint Policies Failed");
  }
}

// Always send the full policyJson, including policyFlow — see SSH_POLICY_FLOWS.
// Update writes a new version with a new id (returned as updatedPolicyID).
export async function updateSSHPolicy({ payload = {}, onSuccess, onFailure }) {
  try {
    const { data: response } = await requests.post(path.updatePolicy, {
      policyType: SSH_CREDENTIAL_TYPE,
      ...payload,
    });
    if (bodyOk(response)) onSuccess && onSuccess(response);
    else onFailure && onFailure(response);
  } catch (error) {
    manageServiceError(error, onFailure, "Update Endpoint Policy Failed");
  }
}

// policyId is the row's id (a UUID string).
export async function deleteSSHPolicy({ orgId, tenantId, policyId, onSuccess, onFailure }) {
  try {
    const { data: response } = await requests.post(path.deletePolicy, {
      orgId,
      tenantId,
      policyId: String(policyId),
    });
    if (bodyOk(response)) onSuccess && onSuccess(response);
    else onFailure && onFailure(response);
  } catch (error) {
    manageServiceError(error, onFailure, "Delete Endpoint Policy Failed");
  }
}

// Suspends a policy: it stops matching and its SSH grants are ended. Fails
// with GRANTS_END_FAILED when the keys can't be revoked.
export async function suspendSSHPolicy({ orgId, tenantId, policyId, onSuccess, onFailure }) {
  try {
    const { data: response } = await requests.post(path.revokePolicy, { orgId, tenantId, policyId: String(policyId) });
    if (bodyOk(response)) onSuccess && onSuccess(response);
    else onFailure && onFailure({ data: response });
  } catch (error) {
    manageServiceError(error, onFailure, "Suspend Endpoint Policy Failed");
  }
}

// Re-activates a suspended policy; the backend re-creates its grants.
export async function resumeSSHPolicy({ orgId, tenantId, policyId, onSuccess, onFailure }) {
  try {
    const { data: response } = await requests.post(path.policyAction, {
      orgId,
      tenantId,
      action: { actionType: "status", actionIds: [String(policyId)], actionValue: "Approved" },
    });
    if (bodyOk(response)) onSuccess && onSuccess(response);
    else onFailure && onFailure({ data: response });
  } catch (error) {
    manageServiceError(error, onFailure, "Resume Endpoint Policy Failed");
  }
}

// The response carries matchReason, which names the near miss when nothing
// matched ("known user, but not that target account"). Surface it verbatim:
// it is the whole difference between "my policy did not fire" and "I have no
// policy", which look identical from outside.
export async function searchSSHPolicy({ payload = {}, onSuccess, onFailure }) {
  try {
    const { data: response } = await requests.post(path.searchPolicy, {
      credentialType: SSH_CREDENTIAL_TYPE,
      ...payload,
    });
    onSuccess && onSuccess(response);
  } catch (error) {
    manageServiceError(error, onFailure, "Search Endpoint Policy Failed");
  }
}
